import { type ReactNode, useEffect, useRef, useState } from "react";
import { setAuthToken } from "../../lib/auth-token.ts";
import { env } from "../../lib/env.ts";
import { resolveIsReturning, resolveLiffEntryPoint, track } from "../analytics/track.ts";
import { mockProfile } from "./mock-profile.ts";
import {
  LiffContext,
  type LiffContextValue,
  type LiffProfile,
  type LiffStatus,
} from "./use-liff.ts";

interface LiffState {
  status: LiffStatus;
  isInClient: boolean;
  isLoggedIn: boolean;
  profile: LiffProfile | null;
  error: string | null;
}

const initialState: LiffState = {
  status: "loading",
  isInClient: false,
  isLoggedIn: false,
  profile: null,
  error: null,
};

export function LiffProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LiffState>(initialState);
  // Guards against firing `liff_opened` twice under StrictMode's dev
  // mount->cleanup->mount, and against re-firing on unrelated re-renders.
  const liffOpenedFired = useRef(false);

  useEffect(() => {
    let cancelled = false;

    function emitLiffOpened() {
      if (liffOpenedFired.current) return;
      liffOpenedFired.current = true;
      const { entryPoint, tableRef } = resolveLiffEntryPoint();
      track("liff_opened", {
        entry_point: entryPoint,
        table_ref: tableRef,
        is_returning: resolveIsReturning(),
      });
    }

    async function bootstrap() {
      // --- Local dev: skip real LIFF, use a mock identity. ---
      if (env.liffMock) {
        setAuthToken("mock-line-access-token");
        if (!cancelled) {
          setState({
            status: "ready",
            isInClient: false,
            isLoggedIn: true,
            profile: mockProfile,
            error: null,
          });
          emitLiffOpened();
        }
        return;
      }

      if (!env.liffId) {
        if (!cancelled) {
          setState({ ...initialState, status: "error", error: "VITE_LIFF_ID is not set" });
        }
        return;
      }

      try {
        const { default: liff } = await import("@line/liff");
        await liff.init({ liffId: env.liffId });

        const isInClient = liff.isInClient();
        const isLoggedIn = liff.isLoggedIn();

        // External browser and not logged in → surface a fallback CTA.
        if (!isLoggedIn) {
          if (!cancelled) {
            setState({
              status: isInClient ? "loading" : "unavailable",
              isInClient,
              isLoggedIn: false,
              profile: null,
              error: null,
            });
          }
          if (isInClient) liff.login();
          return;
        }

        // Logged in → capture token (server-verified) + profile.
        // Prefer ID token (liff.getIDToken()) for direct sub/aud/exp verify per ADR-0005/arch/07.
        // Fall back to access token. Never send getProfile().userId as identity claim.
        const idToken = liff.getIDToken();
        setAuthToken(idToken ?? liff.getAccessToken() ?? undefined);
        const p = await liff.getProfile();
        if (!cancelled) {
          setState({
            status: "ready",
            isInClient,
            isLoggedIn: true,
            profile: {
              userId: p.userId,
              displayName: p.displayName,
              pictureUrl: p.pictureUrl,
              statusMessage: p.statusMessage,
            },
            error: null,
          });
          emitLiffOpened();
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            ...initialState,
            status: "error",
            error: err instanceof Error ? err.message : "LIFF initialization failed",
          });
        }
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = () => {
    if (env.liffMock) return;
    void import("@line/liff").then(({ default: liff }) => liff.login());
  };

  const value: LiffContextValue = {
    ...state,
    isMock: env.liffMock,
    login,
  };

  return <LiffContext value={value}>{children}</LiffContext>;
}
